import { Modal, ModalBody, ModalFooter } from 'react-bootstrap'
import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'
import { WizardFooter } from './WizardFooter'
import { Step1CourseSearch } from './steps/Step1CourseSearch'
import { Step1Ngf } from './steps/Step1Ngf'
import { Step1WhsUk } from './steps/Step1WhsUk'
import { Step1GenericWhs } from './steps/Step1GenericWhs'
import { Step2CopyData } from './steps/Step2CopyData'
import { Step3MatchTees } from './steps/Step3MatchTees'
import { Step4AdditionalTees } from './steps/Step4AdditionalTees'

const SOURCE_CONFIGS = {
  crs: {
    title: () => window.I18n.t('courses.bootstrap_courses.import_from_usga'),
    importUrlKey: 'importCrsUrl',
    hasWizard: true,
    courseIdField: 'crs_course_id',
  },
  scorecentre: {
    title: () => window.I18n.t('courses.bootstrap_courses.import_from_score_centre'),
    importUrlKey: 'importScorecentreUrl',
    hasWizard: true,
    courseIdField: 'scorecentre_course_id',
  },
  golf_advisor: {
    title: () => window.I18n.t('courses.bootstrap_courses.import_from_golf_now'),
    importUrlKey: 'importGolfAdvisorUrl',
    hasWizard: false,
    courseIdField: 'course_id',
  },
  ngf: {
    title: (terminology) => window.I18n.t('courses.bootstrap_courses.import_from', { integration_name: terminology }),
    importUrlKey: 'importNgfUrl',
    hasWizard: false,
    courseIdField: 'course_id',
  },
  whs_uk: {
    title: () => window.I18n.t('courses.bootstrap_courses.import_from_whs'),
    importUrlKey: 'importWhsUkUrl',
    hasWizard: false,
    courseIdField: 'course_uid',
  },
  generic_whs: {
    title: (terminology) => window.I18n.t('courses.bootstrap_courses.import_from', { integration_name: terminology }),
    importUrlKey: 'importGenericWhsUrl',
    hasWizard: false,
    courseIdField: 'course_id',
  },
}

const DEFAULT_COPY_OPTIONS = {
  course_name_abbr: true,
  tee_time_interval: true,
  green_depth: true,
  hole_labels: true,
  shotgun_priority: true,
  pace_of_play: true,
  default_tee_by_gender: true,
  default_tee_by_course: true,
  tee_name_abbr: true,
  tee_color: true,
  stroke_index: false,
  par: false,
  yardage: true,
  tee_pace_of_play: true,
  tee_hole_labels: true,
}

const buildImportParams = (source, state) => {
  const config = SOURCE_CONFIGS[source]
  const { courseIdField } = config

  if (source === 'ngf') {
    return {
      urlKey: config.importUrlKey,
      club_id: state.selectedClubId,
      course_id: state.selectedCourseId,
      course_name: state.selectedCourseName,
    }
  }

  if (source === 'whs_uk') {
    return {
      urlKey: config.importUrlKey,
      course_uid: state.selectedCourseId,
      course_name: state.selectedCourseName,
    }
  }

  if (!config.hasWizard) {
    return {
      urlKey: config.importUrlKey,
      [courseIdField]: state.selectedCourseId,
      course_name: state.selectedCourseName,
    }
  }

  // Wizard sources (crs, scorecentre)
  const params = {
    urlKey: config.importUrlKey,
    [courseIdField]: state.selectedCourseId,
    copy_course_information: state.copyInfo ? '1' : '0',
    tee_ids_to_import: state.selectedTeeIds,
  }

  if (source === 'crs') {
    params.crs_course_name = state.selectedCourseName
  }

  if (state.copyInfo) {
    params.existing_course_id = state.existingCourseId
    params.archive_old_course = state.archiveOldCourse ? '1' : '0'

    Object.entries(state.copyOptions).forEach(([key, val]) => {
      params[key] = val ? '1' : '0'
    })

    Object.entries(state.matchedTees).forEach(([crsId, existingId]) => {
      params[`tee_ids[${crsId}]`] = existingId
    })

    params.additional_tee_ids = state.additionalTeeIds
  }

  return params
}

export const ImportCourseModal = ({ show, setShow, source }) => {
  const config = SOURCE_CONFIGS[source]
  const routes = useSelector((state) => state?.misc?.data?.routes || {})
  const ngfTerminology = useSelector((state) => state?.misc?.data?.ngfTerminology || 'NGF')
  const genericWhsTerminology = useSelector((state) => state?.misc?.data?.genericWhsTerminology || 'WHS')
  const terminologyBySource = { ngf: ngfTerminology, generic_whs: genericWhsTerminology }
  const [step, setStep] = useState(1)
  const [selectedCourseId, setSelectedCourseId] = useState(null)
  const [selectedCourseName, setSelectedCourseName] = useState(null)
  const [selectedTeeIds, setSelectedTeeIds] = useState([])
  const [selectedClubId, setSelectedClubId] = useState(null)
  const [levelSelections, setLevelSelections] = useState({})
  const [copyInfo, setCopyInfo] = useState(false)
  const [existingCourseId, setExistingCourseId] = useState(null)
  const [archiveOldCourse, setArchiveOldCourse] = useState(true)
  const [copyOptions, setCopyOptions] = useState(DEFAULT_COPY_OPTIONS)
  const [matchedTees, setMatchedTees] = useState({})
  const [additionalTeeIds, setAdditionalTeeIds] = useState([])

  const state = {
    selectedCourseId,
    selectedCourseName,
    selectedTeeIds,
    selectedClubId,
    levelSelections,
    copyInfo,
    existingCourseId,
    archiveOldCourse,
    copyOptions,
    matchedTees,
    additionalTeeIds,
  }

  // Compute total steps
  const totalSteps = config.hasWizard && copyInfo ? 4 : config.hasWizard ? 2 : 1

  const canGoNext = (() => {
    if (step === 1) {
      if (source === 'ngf') return !!selectedCourseId && !!selectedClubId
      if (source === 'generic_whs') return !!selectedCourseId
      return !!selectedCourseId
    }
    if (step === 2) return !copyInfo || !!existingCourseId
    return true
  })()

  // Step 2 for wizard sources: show Import if !copyInfo, Next if copyInfo
  const showImportOnStep2 = config.hasWizard && step === 2 && !copyInfo

  const handleClose = () => {
    setStep(1)
    setSelectedCourseId(null)
    setSelectedCourseName(null)
    setSelectedTeeIds([])
    setSelectedClubId(null)
    setLevelSelections({})
    setCopyInfo(false)
    setExistingCourseId(null)
    setArchiveOldCourse(true)
    setCopyOptions(DEFAULT_COPY_OPTIONS)
    setMatchedTees({})
    setAdditionalTeeIds([])
    setShow(false)
  }

  const handleNext = () => {
    if (step < totalSteps) setStep((s) => s + 1)
  }

  const handleBack = () => {
    if (step > 1) setStep((s) => s - 1)
  }

  const handleImport = () => {
    const { urlKey, ...formParams } = buildImportParams(source, state)
    const url = routes[urlKey]

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = url

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    const addInput = (name, value) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    }

    Object.entries(formParams).forEach(([key, value]) => {
      if (Array.isArray(value)) {
        value.forEach((v) => addInput(`${key}[]`, v))
      } else {
        addInput(key, value)
      }
    })

    document.body.appendChild(form)
    form.submit()
  }

  const handleCourseSelect = (value, label) => {
    setSelectedCourseId(value)
    setSelectedCourseName(label)
    setMatchedTees({})
    setAdditionalTeeIds([])
  }

  const handleLevelSelect = (level, value, _label, childLevels) => {
    setLevelSelections((prev) => {
      const next = { ...prev, [level]: value }
      childLevels.forEach((l) => { delete next[l] })
      return next
    })
  }

  const handleCopyOptionChange = (key, value) => {
    setCopyOptions((prev) => ({ ...prev, [key]: value }))
  }

  const handleTeeMatch = (crsId, existingId) => {
    setMatchedTees((prev) => ({ ...prev, [crsId]: existingId }))
  }

  const handleAdditionalTeeToggle = (teeId, checked) => {
    setAdditionalTeeIds((prev) =>
      checked ? [...prev, teeId] : prev.filter((id) => id !== teeId)
    )
  }

  const renderStep = () => {
    if (step === 1) {
      if (source === 'ngf') {
        return (
          <Step1Ngf
            selectedClubId={selectedClubId}
            onClubSelect={setSelectedClubId}
            selectedCourseId={selectedCourseId}
            onCourseSelect={handleCourseSelect}
          />
        )
      }
      if (source === 'whs_uk') {
        return <Step1WhsUk onCourseSelect={handleCourseSelect} />
      }
      if (source === 'generic_whs') {
        return (
          <Step1GenericWhs
            levelSelections={levelSelections}
            onLevelSelect={handleLevelSelect}
            onCourseSelect={handleCourseSelect}
          />
        )
      }
      return (
        <Step1CourseSearch
          source={source}
          onCourseSelect={handleCourseSelect}
          selectedCourseId={selectedCourseId}
          onTeesSelect={setSelectedTeeIds}
          selectedTeeIds={selectedTeeIds}
        />
      )
    }

    if (step === 2 && config.hasWizard) {
      return (
        <Step2CopyData
          copyInfo={copyInfo}
          onCopyInfoChange={setCopyInfo}
          existingCourseId={existingCourseId}
          onExistingCourseChange={setExistingCourseId}
          archiveOldCourse={archiveOldCourse}
          onArchiveOldCourseChange={setArchiveOldCourse}
          copyOptions={copyOptions}
          onCopyOptionChange={handleCopyOptionChange}
        />
      )
    }

    if (step === 3 && config.hasWizard) {
      return (
        <Step3MatchTees
          source={source}
          selectedCourseId={selectedCourseId}
          selectedCourseName={selectedCourseName}
          existingCourseId={existingCourseId}
          selectedTeeIds={selectedTeeIds}
          matchedTees={matchedTees}
          onTeeMatch={handleTeeMatch}
        />
      )
    }

    if (step === 4 && config.hasWizard) {
      return (
        <Step4AdditionalTees
          source={source}
          existingCourseId={existingCourseId}
          unmatchedTeeIds={Object.keys(matchedTees).filter((id) => matchedTees[id] === '-1')}
          additionalTeeIds={additionalTeeIds}
          onAdditionalTeeToggle={handleAdditionalTeeToggle}
        />
      )
    }

    return null
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="center-modal"
      backdrop="static"
    >
      <div className="courses-modal-title-container">
        <div className="courses-modal-title">
          {config.title(terminologyBySource[source])}
        </div>
      </div>
      <ModalBody className="courses-modal-body-container">
        {renderStep()}
      </ModalBody>
      <ModalFooter>
        <WizardFooter
          step={step}
          totalSteps={totalSteps}
          onCancel={handleClose}
          onBack={handleBack}
          onNext={handleNext}
          onImport={handleImport}
          canGoNext={canGoNext}
          showImportOnStep2={showImportOnStep2}
        />
      </ModalFooter>
    </Modal>
  )
}

ImportCourseModal.propTypes = {
  show: PropTypes.bool.isRequired,
  setShow: PropTypes.func.isRequired,
  source: PropTypes.oneOf(['crs', 'scorecentre', 'golf_advisor', 'ngf', 'whs_uk', 'generic_whs']).isRequired,
}
